import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { APISource, ResearchDomainTemplate } from '@/lib/types';
import {
  ArrowRight,
  ChartLine,
  Code,
  Heart,
  Leaf,
  Lightbulb,
  TrendUp,
  Users,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface ResearchDomainTemplatesProps {
  templates: ResearchDomainTemplate[];
  onTemplateSelect: (template: ResearchDomainTemplate) => void;
}

const iconMap = {
  Heart,
  Leaf,
  TrendUp,
  Code,
  Users,
  Lightbulb,
  ChartLine,
};

export function ResearchDomainTemplates({
  templates,
  onTemplateSelect,
}: ResearchDomainTemplatesProps) {
  const [selectedTemplate, setSelectedTemplate] =
    useState<ResearchDomainTemplate | null>(null);

  const handleTemplatePreview = (template: ResearchDomainTemplate) => {
    setSelectedTemplate(template);
  };

  const handleApplyTemplate = (template: ResearchDomainTemplate) => {
    onTemplateSelect(template);
    setSelectedTemplate(null);
    toast.success(`Applied ${template.name} research template`);
  };

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {templates.map(template => {
          const IconComponent =
            iconMap[template.icon as keyof typeof iconMap] || Lightbulb;

          return (
            <Card
              key={template.id}
              className='hover:shadow-lg transition-all duration-200 cursor-pointer group h-full flex flex-col overflow-hidden'
              onClick={() => handleTemplatePreview(template)}
            >
              <CardHeader className='pb-3 flex-shrink-0'>
                <div className='flex items-start justify-between mb-2 min-w-0 overflow-hidden'>
                  <div
                    className={`p-2.5 rounded-lg flex-shrink-0 ${
                      template.color === 'quality-good'
                        ? 'bg-green-100'
                        : template.color === 'insight-green'
                          ? 'bg-emerald-100'
                          : template.color === 'analysis-orange'
                            ? 'bg-orange-100'
                            : template.color === 'primary'
                              ? 'bg-primary/10'
                              : template.color === 'accent'
                                ? 'bg-accent/10'
                                : 'bg-muted'
                    }`}
                  >
                    <IconComponent
                      className={`w-5 h-5 ${
                        template.color === 'quality-good'
                          ? 'text-green-700'
                          : template.color === 'insight-green'
                            ? 'text-emerald-700'
                            : template.color === 'analysis-orange'
                              ? 'text-orange-700'
                              : template.color === 'primary'
                                ? 'text-primary'
                                : template.color === 'accent'
                                  ? 'text-accent'
                                  : 'text-muted-foreground'
                      }`}
                    />
                  </div>
                  <ArrowRight className='w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0' />
                </div>
                <CardTitle className='text-base font-semibold leading-tight mb-1 break-words'>
                  {template.name}
                </CardTitle>
                <CardDescription className='text-sm leading-tight line-clamp-2 break-words'>
                  {template.description}
                </CardDescription>
              </CardHeader>

              <CardContent className='pt-0 flex-1 flex flex-col min-h-0 overflow-hidden'>
                <div className='flex-1 space-y-3 min-h-0 overflow-hidden'>
                  <div className='overflow-hidden'>
                    <p className='text-xs font-medium text-muted-foreground mb-2'>
                      COMMON ANALYSES
                    </p>
                    <div className='flex flex-wrap gap-1 overflow-hidden'>
                      {template.commonAnalyses.slice(0, 2).map(analysis => (
                        <Badge
                          key={analysis}
                          variant='secondary'
                          className='text-xs flex-shrink-0'
                        >
                          {analysis}
                        </Badge>
                      ))}
                      {template.commonAnalyses.length > 2 && (
                        <Badge
                          variant='outline'
                          className='text-xs flex-shrink-0'
                        >
                          +{template.commonAnalyses.length - 2} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className='text-xs font-medium text-muted-foreground mb-1'>
                      DATASETS
                    </p>
                    <p className='text-sm text-foreground'>
                      {template.recommendedDatasets.length} recommended datasets
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Template Preview Dialog */}
      <Dialog
        open={!!selectedTemplate}
        onOpenChange={() => setSelectedTemplate(null)}
      >
        <DialogContent className='max-w-3xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-3'>
              {selectedTemplate &&
                (() => {
                  const IconComponent =
                    iconMap[selectedTemplate.icon as keyof typeof iconMap] ||
                    Lightbulb;

                  return (
                    <>
                      <div
                        className={`p-2 rounded-lg ${
                          selectedTemplate.color === 'quality-good'
                            ? 'bg-green-100'
                            : selectedTemplate.color === 'insight-green'
                              ? 'bg-emerald-100'
                              : selectedTemplate.color === 'analysis-orange'
                                ? 'bg-orange-100'
                                : selectedTemplate.color === 'primary'
                                  ? 'bg-primary/10'
                                  : selectedTemplate.color === 'accent'
                                    ? 'bg-accent/10'
                                    : 'bg-muted'
                        }`}
                      >
                        <IconComponent
                          className={`w-5 h-5 ${
                            selectedTemplate.color === 'quality-good'
                              ? 'text-green-700'
                              : selectedTemplate.color === 'insight-green'
                                ? 'text-emerald-700'
                                : selectedTemplate.color === 'analysis-orange'
                                  ? 'text-orange-700'
                                  : selectedTemplate.color === 'primary'
                                    ? 'text-primary'
                                    : selectedTemplate.color === 'accent'
                                      ? 'text-accent'
                                      : 'text-muted-foreground'
                          }`}
                        />
                      </div>
                      {selectedTemplate.name}
                    </>
                  );
                })()}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className='space-y-6'>
              {/* Common Analyses */}
              <div>
                <h4 className='font-semibold mb-3 flex items-center gap-2'>
                  <ChartLine className='w-4 h-4' />
                  Common Research Questions
                </h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  {selectedTemplate.commonAnalyses.map(analysis => (
                    <div key={analysis} className='p-3 border rounded-lg'>
                      <p className='text-sm font-medium'>{analysis}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Example Questions */}
              <div>
                <h4 className='font-semibold mb-3 flex items-center gap-2'>
                  <Lightbulb className='w-4 h-4' />
                  Example Research Questions
                </h4>
                <div className='space-y-2'>
                  {selectedTemplate.exampleQuestions.map((question, index) => (
                    <div key={index} className='p-3 bg-muted/50 rounded-lg'>
                      <p className='text-sm'>{question}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Datasets */}
              <div>
                <h4 className='font-semibold mb-3 flex items-center gap-2'>
                  <Users className='w-4 h-4' />
                  Recommended Datasets
                </h4>
                <div className='flex flex-wrap gap-2'>
                  {selectedTemplate.recommendedDatasets.map(datasetId => (
                    <Badge key={datasetId} variant='outline'>
                      {datasetId
                        .replace(/-/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Keywords */}
              <div>
                <h4 className='font-semibold mb-3'>Search Keywords</h4>
                <div className='flex flex-wrap gap-2'>
                  {selectedTemplate.keywords.map(keyword => (
                    <Badge
                      key={keyword}
                      variant='secondary'
                      className='text-xs'
                    >
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <div className='flex justify-end pt-4 border-t'>
                <Button
                  onClick={() => handleApplyTemplate(selectedTemplate)}
                  className='gap-2'
                >
                  <ArrowRight className='w-4 h-4' />
                  Apply This Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
